/**
 * Waitlist query + mutation layer. The list keys under `["venue", venueId, "waitlist"]` so the shell's
 * `eventQueryKeyMap` (`waitlist.*` → `["venue", venueId, "waitlist"]`) and the reconnect snapshot
 * invalidate it for free: every entry action emits a `waitlist.*` event, so another waiter's tap
 * refetches here within ~1-2s (realtime-sync target). A 409 (stale CAS) surfaces the ApiError so the
 * view can prompt a refresh (the canonical stale-write rule).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { waitlistApi, type WaitlistEntry, type WaitlistListResponse } from "./waitlistApi";

export const waitlistKeys = {
  list: (venueId: string) => ["venue", venueId, "waitlist", "open"] as const,
};

export function useWaitlist(venueId: string) {
  return useQuery<WaitlistListResponse>({
    queryKey: waitlistKeys.list(venueId),
    queryFn: ({ signal }) => waitlistApi.list(venueId, signal),
    staleTime: 10_000,
  });
}

/** A mutation that refetches the open list on settle (belt) — the SSE `waitlist.*` event is the suspenders. */
export function useWaitlistMutation<T>(
  venueId: string,
  fn: (args: T) => Promise<WaitlistEntry | unknown>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSettled: () => qc.invalidateQueries({ queryKey: waitlistKeys.list(venueId) }),
  });
}
